import classNames from 'classnames';
import _find from 'lodash/find';
import _filter from 'lodash/filter';
import _get from 'lodash/get';
import _uniq from 'lodash/uniq';
import _flattenDeep from 'lodash/flattenDeep';
import _sumBy from 'lodash/sumBy';
import _isEmpty from 'lodash/isEmpty';
import _noop from 'lodash/noop';
import _toNumber from 'lodash/toNumber';
import dayjs from '../../utils/dayjs';
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  isAssignmentInProgress,
  getToolIds,
} from '@macmillan-learning/gradebook-shared';
import s from './AssignmentHeaderLabelAndTool.module.css';
import ToolLabel from '../../components/ToolLabel/ToolLabel';
import { GRADING_TYPE } from '../../utils/constants';
import Sorter from '../InstructorGradebookContainer/Components/Sorter/Sorter';
import MLToolLaunchLink from '../../components/MLToolLaunchLink';
import ManualAssignmentActions from '../InstructorGradebookContainer/Components/ManualAssigmentActions';
import MLTooltip from '../MLTooltip';

const { reef, manual } = getToolIds();
const isNonLinkableTool = { reef, manual };

const TooltipContainer = forwardRef(
  ({ children, isLink = false, ...props }, ref) => {
    return (
      <div {...props} ref={ref} className={s.triggerContainer}>
        <div
          className={classNames(s.assignmentTextLimit, s.triggerContainer, {
            [s.link]: isLink,
          })}
        >
          {children}
        </div>
      </div>
    );
  },
);

const renderAssignmentLabel = (
  assignmentNameStyle = '',
  assignment,
  isLinkDisabled,
  isAdminOrInstructor,
  tooltipContent,
  truncateClass,
  getLaunchUrl,
  handleLocalLaunch,
  isStudent,
  isIndividualStudentGradebook,
  hasInstructorRestrictedCourseLevelRole,
  shouldSidePanelBeActive,
  isDropdownOpen,
) => {
  const {
    manual: manualToolId,
    staticfile,
    instructorfile,
    url,
    iclicker,
  } = getToolIds();

  const isISGLinkDisabled =
    isIndividualStudentGradebook &&
    [staticfile, instructorfile, url, manualToolId, iclicker].includes(
      assignment.tool,
    );

  const includeManualAssignmentSorter =
    assignment.tool === manualToolId &&
    (assignment.grading_type === GRADING_TYPE.GRADED ||
      assignment.grading_type === GRADING_TYPE.EXTRA_CREDIT);

  if (
    (assignment.tool === manualToolId && !isAdminOrInstructor) ||
    (!includeManualAssignmentSorter &&
      (_isEmpty(assignment.tool) || isNonLinkableTool[assignment.tool]))
  ) {
    const trigger = <span>{assignment.headerContent || assignment.name}</span>;
    return (
      <MLTooltip
        content={tooltipContent}
        placement="top"
        disabled={isDropdownOpen}
      >
        <TooltipContainer>{trigger}</TooltipContainer>
      </MLTooltip>
    );
  }

  if (shouldSidePanelBeActive) {
    const trigger = (
      <span className={isISGLinkDisabled ? '' : s.link}>
        {assignment.headerContent || assignment.name}
      </span>
    );
    return (
      <MLTooltip
        content={tooltipContent}
        placement="top"
        disabled={isDropdownOpen}
      >
        <TooltipContainer>{trigger}</TooltipContainer>
      </MLTooltip>
    );
  }

  if (includeManualAssignmentSorter && !isIndividualStudentGradebook) {
    return (
      <div className={`${s.assignmentTextLimit} ${truncateClass}`}>
        <ManualAssignmentActions
          showAsLabel
          assignment={assignment}
          tooltipContent={tooltipContent}
          disableActions={hasInstructorRestrictedCourseLevelRole}
        />
      </div>
    );
  }

  const trigger = (
    <span>
      <MLToolLaunchLink
        className={`${assignmentNameStyle} ${s.link}`}
        isLinkDisabled={isLinkDisabled || isISGLinkDisabled}
        data={{
          ...assignment,
          tab: 'performance',
        }}
        testId="assignment_link"
        linkLabel={assignment.name}
        getLaunchUrl={getLaunchUrl}
        handleLocalLaunch={handleLocalLaunch}
        isStudent={isStudent}
      />
    </span>
  );

  return (
    <MLTooltip
      content={tooltipContent}
      placement={isIndividualStudentGradebook ? 'top-start' : 'top'}
      disabled={isDropdownOpen}
    >
      <TooltipContainer isLink={!isISGLinkDisabled}>{trigger}</TooltipContainer>
    </MLTooltip>
  );
};

const defaultSortOptions = {
  active: false,
  direction: 'asc',
  onChangeSort: _noop,
};

const getAriaSettings = (assignment) => {
  const {
    id,
    isCourseTotalColumn,
    tool,
    name,
    grading_type: gradingType,
    gradebook_category_id: categoryId,
    gradebook_category_name: categoryName,
  } = assignment;

  if (id === '-1')
    return {
      key: 'ungradedTotal',
      description: 'Ungraded Category Total',
    };
  if (isCourseTotalColumn)
    return { key: 'courseTotal', description: 'Course Total' };
  if (_toNumber(id) === categoryId)
    return {
      key: `category_${assignment.id}`,
      description: `${categoryName} Category Total`,
    };
  if (tool === getToolIds().manual && gradingType === GRADING_TYPE.graded)
    return {
      key: `manual_assignment_${id}`,
      description: `${name}`,
    };
  if (categoryId === -1)
    return {
      key: `ungraded_assignment_${id}`,
      description: `${name}`,
    };

  return { key: 'assignmentSorter', description: name || 'Assignment' };
};

const ExceptionLabel = ({ numExceptions, isAdminOrInstructor }) => {
  if (numExceptions === 0) return null;

  if (isAdminOrInstructor)
    return (
      <span>
        {numExceptions} exception{numExceptions > 1 ? 's' : ''}
      </span>
    );

  return <span>1 exception</span>; // if numException is greater than 0 and is a student ever will show only 1 exception (for the moment we only allow 1 exception by student in each assignment)
};

ExceptionLabel.propTypes = {
  numExceptions: PropTypes.number.isRequired,
  isAdminOrInstructor: PropTypes.bool,
};

const AssignmentHeaderLabelAndTool = ({
  user,
  assignmentNameStyle,
  assignment,
  truncate = false,
  isLinkDisabled = false,
  sortOptions,
  getLaunchUrl,
  handleLocalLaunch,
  isStudent,
  isIndividualStudentGradebook,
  showDueDate = true,
  shouldSidePanelBeActive,
  isDropdownOpen,
}) => {
  const isAdminOrInstructor =
    user && (user.role === 'admin' || user.role === 'instructor');

  const hasInstructorRestrictedCourseLevelRole = _get(
    user,
    'has_instructor_restricted_course_level_role',
    false,
  );

  let dueDate =
    _get(
      _find(assignment.Exceptions, (exception) =>
        ['assignment', 'due_date'].includes(exception.exception_type),
      ),
      'exception_value.due_date',
    ) || assignment.assignment_due_at; // if is a student we should check the assignment and the due_date exceptions
  if (isAdminOrInstructor) {
    dueDate =
      _get(
        _find(assignment.Exceptions, { exception_type: 'assignment' }),
        'exception_value.due_date',
      ) || assignment.assignment_due_at;
  }
  const dueDateFmt = dayjs(dueDate).format('MMM D, YYYY');
  const isManualAssignment = assignment.tool === getToolIds().manual;
  const exceptions =
    _filter(assignment.Exceptions, { exception_type: 'due_date' }) || [];
  const usersExceptions = _uniq(
    _flattenDeep(exceptions.map((exception) => exception.user_ids)),
  );
  const numExceptions = _sumBy(usersExceptions, (userId) => userId !== null);
  const exceptionsMkp = (
    <ExceptionLabel
      isAdminOrInstructor={isAdminOrInstructor}
      numExceptions={+numExceptions}
    />
  );
  const truncateClass = truncate ? s.truncate : '';
  const tooltipContent = assignment.name || assignment.headerContent;
  const inProgressLabel = !isManualAssignment &&
    isAssignmentInProgress(assignment) && (
      <div
        className={s.inProgress}
        data-test-id={`${tooltipContent}-in-progress`}
      >
        (In Progress)
      </div>
    );

  const toolLabel = _isEmpty(assignment.tool) ? null : (
    <div>
      <ToolLabel
        activity={assignment}
        classNames={s.toolLabel}
        containerClass={s.toolLabelOverride}
        isAdminOrInstructor={isAdminOrInstructor}
        inProgressLabel={inProgressLabel}
        isIndividualStudentGradebook={isIndividualStudentGradebook}
      />
    </div>
  );

  const showManualAssignmentControls =
    isAdminOrInstructor && isManualAssignment;
  const showSorterControl = assignment.includeSorter && !!sortOptions;
  const finalSortOptions = {
    ...defaultSortOptions,
    ...sortOptions,
  };
  const categoryTotalClass =
    showSorterControl &&
    (assignment.id === '-1' ||
      assignment.isCourseTotalColumn ||
      _toNumber(assignment.id) === assignment.gradebook_category_id)
      ? s.categoryTotalContainer
      : '';

  return (
    <div
      className={`${s.assignmentHeader} ${
        showSorterControl ? s.assignmentHeaderWithSorter : ''
      }`}
    >
      <div
        className={classNames(categoryTotalClass, s.assignmentHeaderContent)}
      >
        {renderAssignmentLabel(
          assignmentNameStyle,
          assignment,
          isLinkDisabled,
          isAdminOrInstructor,
          tooltipContent,
          truncateClass,
          getLaunchUrl,
          handleLocalLaunch,
          isStudent,
          isIndividualStudentGradebook,
          hasInstructorRestrictedCourseLevelRole,
          shouldSidePanelBeActive,
          isDropdownOpen,
        )}
        {toolLabel}
        {!assignment.isAggregateCell && showDueDate && (
          <div
            className={s.dateStyle}
            data-test-id={`${tooltipContent}-due-date`}
          >
            Due Date:&nbsp;
            <span className={s.dateContent}>{dueDateFmt}</span>
            {exceptionsMkp && (
              <div data-test-id={`${tooltipContent}-exception`}>
                {exceptionsMkp}
              </div>
            )}
          </div>
        )}
      </div>
      {showManualAssignmentControls &&
        !showSorterControl &&
        !isIndividualStudentGradebook && (
          <ManualAssignmentActions
            disableActions={hasInstructorRestrictedCourseLevelRole}
            assignment={assignment}
          />
        )}
      {showSorterControl && (
        <div
          className={`${s.sorterContainer}`}
          data-test-id={`${tooltipContent}-sort`}
        >
          <Sorter
            active={finalSortOptions.active}
            sortDirection={finalSortOptions.direction}
            handleSort={finalSortOptions.onChangeSort}
            ariaSettings={getAriaSettings(assignment)}
          />
        </div>
      )}
    </div>
  );
};

AssignmentHeaderLabelAndTool.propTypes = {
  assignment: PropTypes.object.isRequired,
  assignmentNameStyle: PropTypes.string,
  truncate: PropTypes.bool,
  isLinkDisabled: PropTypes.bool,
  user: PropTypes.object,
  getLaunchUrl: PropTypes.func,
  handleLocalLaunch: PropTypes.func,
  isStudent: PropTypes.bool,
  shouldSidePanelBeActive: PropTypes.bool,
  sortOptions: PropTypes.shape({
    active: PropTypes.bool,
    direction: PropTypes.arrayOf(PropTypes.oneOf(['asc', 'desc'])),
    onChangeSort: PropTypes.func,
  }),
};

export default AssignmentHeaderLabelAndTool;
